import React, { useEffect, useId, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Popper, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getLeadErrorMessage, getLeads, readLeadsList } from '../../services/leadsService';
import LeadTemperatureDot from './LeadTemperatureDot';
import LeadSearchAdornment, { LEAD_SEARCH_ICON_SLOT } from './LeadSearchAdornment';
import { FLOATING_MODIFIERS, floatingPopperSx, leadRecordDropdownSx } from './LeadRecordSearch';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import {
  LEADS_SELECTED_ACCENT,
  LEADS_TEXT_MUTED,
  LEADS_TEXT_PRIMARY,
  LEADS_TEXT_SECONDARY,
  leadsTextFieldSx,
} from './leadsUi.styles';

export const LEAD_PICKER_DEBOUNCE_MS = 300;
const LEAD_PICKER_LIMIT = 8;

// Flotante sobre el diálogo (Popper en portal: no lo recorta el scroll del
// contenido ni cambia su alto), ancho del input, tope ~3 filas con scroll y fade.
const leadPickerResultsSx = {
  position: 'static',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
  maxHeight: 156,
  overflowY: 'auto',
};

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  width: '100%',
  px: 1,
  py: 0.75,
  borderRadius: '6px',
  textTransform: 'none',
  justifyContent: 'flex-start',
  textAlign: 'left',
  color: LEADS_TEXT_PRIMARY,
  cursor: 'pointer',
  '&:hover, &[data-active="true"]': { bgcolor: 'rgba(255,255,255,0.06)' },
};
// Lead elegido: mismo lugar y alto que el input (40 px, radio 8), borde teal
// del chip activo; punto de temperatura (en el lugar de la lupa) + nombre +
// teléfono · email (alineados con el texto del input) + ✕.
const selectedLeadSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  height: 40,
  pl: '13px',
  pr: 0.5,
  borderRadius: '8px',
  border: `1px solid ${LEADS_SELECTED_ACCENT}`,
  bgcolor: `${LEADS_SELECTED_ACCENT}1A`,
  minWidth: 0,
};
const selectedClearSx = {
  width: 32,
  height: 32,
  flexShrink: 0,
  ml: 'auto',
  color: LEADS_TEXT_SECONDARY,
  '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
};

const metaSx = { fontSize: '11px', color: LEADS_TEXT_MUTED, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

/** “Ana Pérez · 351 555 · ana@x.com” para el chip del elegido. */
export function describePickedLead(lead) {
  return [lead?.name, lead?.phone, lead?.email].filter(Boolean).join(' · ');
}

/**
 * Buscador de leads existentes (`/getleads?q=`, debounce 300 ms, ≤8). Elegir →
 * chip con ✕ (o fijo con `locked`, desde la ficha del lead).
 *
 * Resultados en un desplegable flotante (combobox + listbox): el foco queda en
 * el input (el diálogo lo atrapa) y se navega con ↑ / ↓ + Enter
 * (`aria-activedescendant`). Escape lo cierra sin cerrar el diálogo; también
 * al salir del input y al elegir.
 *
 * @param {{ value: object|null, onChange: (lead: object|null) => void,
 *   ownerUserId?: string|null, locked?: boolean, disabled?: boolean,
 *   error?: string }} props
 */
export default function LeadPicker({
  value = null,
  onChange,
  ownerUserId = null,
  locked = false,
  disabled = false,
  error = '',
}) {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [state, setState] = useState({ status: 'idle', leads: [], error: '' });
  // Cerrado a mano (Escape, blur); se reabre al escribir o enfocar.
  const [dismissed, setDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  // Tras «Quitar lead» el input vuelve con foco.
  const focusInputRef = useRef(false);
  const listId = useId();
  const {
    scrollRef: resultsScrollRef, fadeTop: resultsFadeTop, fadeBottom: resultsFadeBottom,
  } = useScrollEdgeFade();

  useEffect(() => { setActiveIndex(-1); }, [state.leads]);

  useEffect(() => {
    if (value || !focusInputRef.current) return;
    focusInputRef.current = false;
    inputRef.current?.focus();
  }, [value]);

  useEffect(() => {
    const handle = setTimeout(() => setQuery(text.trim()), LEAD_PICKER_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [text]);

  useEffect(() => {
    if (!query || value) {
      setState({ status: 'idle', leads: [], error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setState((prev) => ({ ...prev, status: 'loading', error: '' }));
    (async () => {
      try {
        const res = await getLeads(
          { q: query, limit: LEAD_PICKER_LIMIT, sort: 'last_touch_desc' },
          { ownerUserId, signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        if (!res?._httpOk) {
          setState({ status: 'error', leads: [], error: getLeadErrorMessage(res, 'No se pudo buscar.') });
          return;
        }
        setState({ status: 'ready', leads: readLeadsList(res).leads, error: '' });
      } catch (err) {
        if (err?.name !== 'AbortError') setState({ status: 'error', leads: [], error: 'No se pudo buscar.' });
      }
    })();
    return () => controller.abort();
  }, [query, ownerUserId, value]);

  if (value) {
    const label = describePickedLead(value);
    const meta = [value.phone, value.email].filter(Boolean).join(' · ');
    return (
      <Box data-testid="lead-picker-selected" sx={selectedLeadSx}>
        <Box sx={{ width: LEAD_SEARCH_ICON_SLOT, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <LeadTemperatureDot temperature={value.temperature} testId="lead-picker-selected-temperature" />
        </Box>
        <Tooltip title={label} placement="top" arrow enterDelay={400}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: LEADS_TEXT_PRIMARY, lineHeight: 1.3 }} noWrap>
              {value.name}
            </Typography>
            {meta ? <Typography sx={metaSx}>{meta}</Typography> : null}
          </Box>
        </Tooltip>
        {locked ? null : (
          <IconButton
            size="small"
            aria-label="Quitar lead"
            disabled={disabled}
            onClick={() => {
              focusInputRef.current = true;
              setText('');
              onChange(null);
            }}
            sx={selectedClearSx}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
    );
  }

  const options = state.status === 'ready' ? state.leads : [];
  const optionId = (index) => `${listId}-opt-${index}`;
  const pickLead = (lead) => {
    setDismissed(true);
    onChange(lead);
  };

  let results = null;
  if (query) {
    if (state.status === 'loading' && !state.leads.length) {
      results = <Box role="status" sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={18} aria-label="Buscando" /></Box>;
    } else if (state.status === 'error') {
      results = <Typography sx={{ ...metaSx, px: 1 }} role="alert">{state.error}</Typography>;
    } else if (state.status === 'ready') {
      results = options.length ? options.map((lead, index) => (
        <Box
          key={lead.id}
          id={optionId(index)}
          role="option"
          aria-selected={index === activeIndex}
          aria-label={`Elegir lead ${lead.name}`}
          data-active={index === activeIndex}
          onMouseEnter={() => setActiveIndex(index)}
          onClick={() => pickLead(lead)}
          sx={rowSx}
        >
          <LeadTemperatureDot temperature={lead.temperature} testId="lead-picker-temperature" />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '13px', color: LEADS_TEXT_PRIMARY, lineHeight: 1.3 }} noWrap>{lead.name}</Typography>
            {(lead.phone || lead.email) && (
              <Typography sx={metaSx}>{[lead.phone, lead.email].filter(Boolean).join(' · ')}</Typography>
            )}
          </Box>
        </Box>
      )) : <Typography role="status" sx={{ ...metaSx, px: 1 }}>No hay leads con ese nombre, email o teléfono.</Typography>;
    }
  }
  const open = Boolean(results) && !dismissed && !disabled;
  const hasOptions = open && options.length > 0;

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && open) {
      // Cierra la lista, no el diálogo.
      e.preventDefault();
      e.stopPropagation();
      setDismissed(true);
      return;
    }
    if (!hasOptions) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next = (activeIndex + step + options.length) % options.length;
      setActiveIndex(next);
      document.getElementById(optionId(next))?.scrollIntoView?.({ block: 'nearest' });
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      pickLead(options[activeIndex]);
    }
  };

  return (
    <Box data-testid="lead-picker">
      <TextField
        ref={anchorRef}
        value={text}
        onChange={(e) => { setText(e.target.value); setDismissed(false); }}
        onFocus={() => setDismissed(false)}
        onBlur={() => setDismissed(true)}
        onKeyDown={handleKeyDown}
        placeholder="Buscar por nombre, email o teléfono"
        size="small"
        fullWidth
        disabled={disabled}
        error={Boolean(error)}
        helperText={error || ''}
        inputRef={inputRef}
        InputProps={{ startAdornment: <LeadSearchAdornment /> }}
        inputProps={{
          'aria-label': 'Buscar lead',
          maxLength: 120,
          role: 'combobox',
          'aria-autocomplete': 'list',
          'aria-expanded': open,
          'aria-controls': open ? listId : undefined,
          'aria-activedescendant': hasOptions && activeIndex >= 0 ? optionId(activeIndex) : undefined,
        }}
        sx={leadsTextFieldSx}
      />
      <Popper
        open={open}
        anchorEl={() => anchorRef.current?.querySelector('.MuiOutlinedInput-root') || anchorRef.current}
        placement="bottom-start"
        modifiers={FLOATING_MODIFIERS}
        sx={floatingPopperSx}
        style={{ width: anchorRef.current?.querySelector('.MuiOutlinedInput-root')?.clientWidth || undefined }}
      >
        <Box
          id={listId}
          ref={resultsScrollRef}
          role={hasOptions ? 'listbox' : 'region'}
          aria-label="Resultados de leads"
          // Scrollear la lista con el mouse no saca el foco del input (no se cierra).
          onMouseDown={(e) => e.preventDefault()}
          sx={{ ...leadRecordDropdownSx, ...leadPickerResultsSx, ...getScrollEdgeFadeSx(resultsFadeTop, resultsFadeBottom) }}
        >
          {results}
        </Box>
      </Popper>
    </Box>
  );
}
